import React, { useRef, useMemo, useState } from 'react';
import { Save, FolderOpen, Undo, Redo, Image as ImageIcon, Eye, Download, Lock, ArrowLeft, ChevronDown, Send } from 'lucide-react';
import { App, Dropdown, Modal, Tooltip } from 'antd';
import { CrownOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { MenuProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { useEditorStore } from '../store/useEditorStore';
import { useProjectStore } from '../store/useProjectStore';
import { useAuthStore, getFeatureFromQuota } from '../stores/auth';
import { exportProjectAsOBJ, exportProjectAsKimap, generateKimapUniqueId } from '../utils/objExporter';
import { exportProjectAsKidataWithId } from '../utils/kidataExporter';
import { publishMap, saveAreaData } from '../utils/mapPublisher';
import LanguageSwitcher from './LanguageSwitcher';
import FeedbackButton from './feedback/FeedbackButton';
import MD5 from 'crypto-js/md5';
import logoImg from '@/assets/kimap-logo.png';

interface TopBarProps {
  onBackToList?: () => void;
  isExternalMode?: boolean;
  isExampleProject?: boolean;
  onProjectLoadingChange?: (loading: boolean) => void;
  externalPocKey?: string;
}

const TopBar: React.FC<TopBarProps> = ({
  onBackToList,
  isExternalMode = false,
  isExampleProject = false,
  onProjectLoadingChange,
  externalPocKey,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const projectFileInputRef = useRef<HTMLInputElement>(null);
  const { message } = App.useApp();
  
  const {
    project,
    currentFloor,
    undo,
    redo,
    history,
    uploadBaseMap,
    updateBaseMap,
    openPreview,
    loadProject,
  } = useEditorStore();
  
  const {
    saveProjectToEncryptedFile,
    loadProjectFromEncryptedFile,
  } = useProjectStore();

  const { quota } = useAuthStore();

  // 密码验证错误状态
  const [passwordError, setPasswordError] = useState('');

  const hasBasemapPerm = getFeatureFromQuota(quota?.permissions, 'basemap');
  const hasExport = getFeatureFromQuota(quota?.permissions, 'export');
  const hasPublish = getFeatureFromQuota(quota?.permissions, 'publish');
  const navigate = useNavigate();

  const PLAN_COLORS: Record<string, string> = {
    Personal: 'bg-blue-50 text-blue-600',
    Professional: 'bg-purple-50 text-purple-600',
    Enterprise: 'bg-amber-50 text-amber-600',
  };

  const upgradeTooltipOverlay = (plans: string[], featureName: string) => (
    <div className="p-2 min-w-[180px]">
      <div className="text-xs text-gray-500 mb-2">{featureName}</div>
      <div className="text-xs text-gray-400 mb-2">{t('permissionCard.availableInPlans')}</div>
      <div className="flex flex-wrap gap-1 mb-3">
        {plans.map((plan) => (
          <span
            key={plan}
            className={`px-2 py-0.5 rounded text-xs font-medium ${PLAN_COLORS[plan] || 'bg-gray-100 text-gray-600'}`}
          >
            {plan}
          </span>
        ))}
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); navigate('/subscription'); }}
        className="w-full py-1.5 bg-primary-500 hover:bg-primary-600 text-white text-xs font-medium rounded transition-colors"
      >
        {t('permissionCard.upgradeNow')}
      </button>
    </div>
  );

  // 检查是否有元素可以预览
  const hasElements = useMemo(() => {
    if (!project || !currentFloor) return false;
    const floor = project.floors.find(f => f.id === currentFloor);
    return floor?.layers.some(layer => layer.elements.length > 0) ?? false;
  }, [project, currentFloor]);

  const handleSave = async () => {
    if (project) {
      try {
        await saveProjectToEncryptedFile(project, project.name);
        message.success(t('topBar.savedSuccess'));
      } catch (error) {
        const detail = error instanceof Error ? error.message : t('topBar.saveFailed');
        message.error(detail);
      }
    }
  };

  const handleOpenProject = () => {
    projectFileInputRef.current?.click();
  };

  const handleProjectFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 验证文件扩展名
    if (!file.name.endsWith('.kimap.tm')) {
      message.warning(t('topBar.selectFile'));
      return;
    }

    try {
      // 显示loading遮罩
      onProjectLoadingChange?.(true);

      // 加载加密文件
      const projectData = await loadProjectFromEncryptedFile(file);

      // 加载项目数据到编辑器
      if (projectData.data) {
        loadProject(projectData.data);
        message.success({ content: t('topBar.loadSuccess', { name: projectData.name }), key: 'loading' });
      } else {
        throw new Error(t('topBar.invalidFormat'));
      }
    } catch (error) {
      console.error('Failed to load project:', error);
      message.error({ content: t('topBar.loadFailed'), key: 'loading' });
    } finally {
      // 隐藏loading遮罩
      onProjectLoadingChange?.(false);
    }

    // 清空input
    if (projectFileInputRef.current) {
      projectFileInputRef.current.value = '';
    }
  };

  const handleExportOBJ = () => {
    if (!project) {
      message.warning(t('topBar.noProject'));
      return;
    }
    if (!hasElements) {
      message.warning(t('topBar.noElements'));
      return;
    }
    try {
      exportProjectAsOBJ(project);
      message.success(t('topBar.objSuccess'));
    } catch (error) {
      console.error('Failed to export OBJ:', error);
      message.error(t('topBar.objFailed'));
    }
  };

  const handlePublishMap = async () => {
    if (!project) {
      message.warning(t('topBar.noProject'));
      return;
    }

    // 每次打开弹窗时清除错误状态
    setPasswordError('');

    // 外部模式下需要验证密码
    if (isExternalMode && externalPocKey) {
      Modal.confirm({
        title: t('topBar.publishMapConfirmTitle'),
        content: (
          <div>
            <p className="mb-3">{t('topBar.publishMapConfirmMessage', { name: project.name })}</p>
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <span className="text-gray-600 text-sm whitespace-nowrap">{t('topBar.enterPassword')}:</span>
                <input
                  type="password"
                  id="publish-password-input"
                  className={`flex-1 px-3 py-1.5 border rounded focus:outline-none focus:border-primary-500 ${passwordError ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder={t('topBar.passwordPlaceholder')}
                  autoFocus
                  onChange={() => setPasswordError('')}
                />
              </div>
              {passwordError && (
                <span className="text-red-500 text-sm ml-[100px]">{passwordError}</span>
              )}
            </div>
          </div>
        ),
        okText: t('dialog.confirm'),
        cancelText: t('dialog.cancel'),
        onOk: async () => {
          const passwordInput = document.getElementById('publish-password-input') as HTMLInputElement;
          const enteredPassword = passwordInput?.value;
          const hashedPassword = MD5(enteredPassword).toString();

          console.log('=== 密码验证 ===');
          console.log('输入的密码:', enteredPassword);
          console.log('MD5加密后:', hashedPassword);
          console.log('URL携带的pocKey:', externalPocKey);
          console.log('================');

          if (hashedPassword !== externalPocKey) {
            setPasswordError(t('topBar.passwordMismatch'));
            return Promise.reject();
          }

          await doPublishMap();
        },
      });
    } else {
      // 普通模式，直接显示确认弹窗
      Modal.confirm({
        title: t('topBar.publishMapConfirmTitle'),
        content: t('topBar.publishMapConfirmMessage', { name: project.name }),
        okText: t('dialog.confirm'),
        cancelText: t('dialog.cancel'),
        onOk: async () => {
          await doPublishMap();
        },
      });
    }
  };

  const doPublishMap = async () => {
    if (!project) return;
    
    try {
      message.loading({ content: t('topBar.publishing'), key: 'publish' });

      const result = await publishMap(project);

      if (result.success) {
        message.success({
          content: result.message || t('topBar.publishMapSuccess'),
          key: 'publish',
        });
        console.log('地图发布成功:', result);

        // 如果是外部模式，发布成功后更新区域数据
        if (isExternalMode && result.uniqueId) {
          const areaStr = sessionStorage.getItem('currentExternalArea');
          if (areaStr) {
            try {
              const area = JSON.parse(areaStr);
              await saveAreaData(area, result.uniqueId);
              console.log('区域数据更新成功');
            } catch (error) {
              console.error('更新区域数据失败:', error);
            }
          }
        }
      } else {
        message.error({
          content: result.message || t('topBar.publishMapFailed'),
          key: 'publish',
        });
      }
    } catch (error) {
      console.error('发布地图失败:', error);
      message.error({
        content: t('topBar.publishMapFailed'),
        key: 'publish',
      });
    }
  };

  const handleExportKimap = async () => {
    if (!project) {
      message.warning(t('topBar.noProject'));
      return;
    }
    if (!hasElements) {
      message.warning(t('topBar.noElements'));
      return;
    }

    // 检查是否有3D家具模型
    const has3DModels = project.floors.some(floor =>
      floor.layers.some(layer =>
        layer.elements.some(el => el.type === 'model3d')
      )
    );

    const uniqueId = generateKimapUniqueId();

    try {
      // 导出 Kimap 和 Kitheme（仍使用原来的同步函数）
      exportProjectAsKimap(project, uniqueId);

      if (has3DModels) {
        // kidata 现在是 async（会并发拉取所有用到的 3D 模型源文件并嵌入）
        // 等待完成后再下载，无需 600ms 等待
        await exportProjectAsKidataWithId(project, uniqueId);
        message.success(t('topBar.kimapSuccess'));
      } else {
        message.success(t('topBar.kimapModelSuccess'));
      }
    } catch (error) {
      console.error('Failed to export Kimap:', error);
      message.error(t('topBar.kimapFailed'));
    }
  };

  const handleImportBaseMap = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 验证文件类型
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      message.warning(t('topBar.uploadImage'));
      return;
    }

    // 验证文件大小（10MB）
    if (file.size > 10 * 1024 * 1024) {
      message.error(t('topBar.imageTooLarge'));
      return;
    }

    try {
      await uploadBaseMap(file);
      message.success({
        content: t('topBar.cadSuccess'),
        duration: 6,
      });
    } catch (error) {
      console.error('Failed to upload base map:', error);
      message.error(t('topBar.cadFailed'));
    }

    // 清空input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const hasBaseMap = project?.mapConfig?.baseMap;
  const hasValidArea = project?.mapConfig?.validArea && project.mapConfig.validArea.length >= 3;
  const isCalibrated = project?.mapConfig?.isCalibrated;

  return (
    <div className="h-10 bg-white border-b border-gray-200 flex items-center justify-between px-2 relative z-50">
      <div className="flex items-center gap-4">
        {/* 返回按钮 */}
        {onBackToList && (
          <button
            onClick={onBackToList}
            className="p-1.5 hover:bg-gray-100 transition text-primary-600"
            title={t('topBar.backToList')}
          >
            <ArrowLeft size={18} />
          </button>
        )}
        <h1 className="h-6">
          <img src={logoImg} alt="Kimap" className="h-full w-auto" />
        </h1>
        <div className="h-6 w-px bg-gray-300" />
        <span className="text-sm text-gray-600">{project?.name || t('app.untitledProject')}</span>
        
        {/* 底图状态指示 */}
        {!hasBaseMap && (
          <div className="flex items-center gap-2 text-sm bg-amber-100 text-amber-800 px-3 py-1.5 rounded-lg border border-amber-200 animate-pulse">
            <span className="text-base">⚠️</span>
            <span className="font-medium">{t('topBar.needCAD')}</span>
          </div>
        )}
        {hasBaseMap && !isCalibrated && (
          <div className="flex items-center gap-2 text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded-lg">
            <span>📏</span>
            <span>{t('topBar.calibrateFirst')}</span>
          </div>
        )}
        {hasBaseMap && isCalibrated && !hasValidArea && (
          <div className="flex items-center gap-2 text-xs bg-amber-50 text-amber-700 px-2 py-1 rounded-lg">
            <span>⚠️</span>
            <span>{t('topBar.drawAreaFirst')}</span>
          </div>
        )}
        {hasBaseMap && isCalibrated && hasValidArea && (
          <div className="flex items-center gap-2 text-xs bg-green-50 text-green-700 px-2 py-1 rounded-lg">
            <span>✓</span>
            <span>{t('topBar.calibrated')}</span>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        {/* File operations */}
        {!isExternalMode && !isExampleProject && (
          <>
            <button
              onClick={handleSave}
              className="p-1.5 hover:bg-gray-100 transition"
              title={t('topBar.saveAs')}
            >
              <Save size={16} />
            </button>
            <button
              onClick={handleOpenProject}
              className="p-1.5 hover:bg-gray-100 transition"
              title={t('topBar.openFile')}
            >
              <FolderOpen size={16} />
            </button>
          </>
        )}
        {/* 底图导入按钮 - 外部模式/例子项目不显示 */}
        {!isExternalMode && !isExampleProject && (
          hasBaseMap ? (
            <div className="relative inline-flex">
              <button
                onClick={handleImportBaseMap}
                className="p-1.5 rounded-lg transition hover:bg-gray-100 bg-primary-50 text-primary-600"
                title={t('topBar.reimportCAD')}
              >
                <ImageIcon size={16} />
              </button>
              {!hasBasemapPerm && (
                <div
                  className="absolute inset-0 z-10 rounded-lg bg-white/60 backdrop-blur-[1px] flex items-center justify-center"
                  onClick={(e) => { e.stopPropagation(); }}
                >
                  <Tooltip
                    color="#fff"
                    placement="bottom"
                    title={upgradeTooltipOverlay(['Personal', 'Professional', 'Enterprise'], t('permissionCard.basemapUpload'))}
                    trigger="hover"
                  >
                    <div className="w-full h-full flex items-center justify-center cursor-pointer">
                      <CrownOutlined className="text-amber-500 text-[10px]" />
                    </div>
                  </Tooltip>
                </div>
              )}
            </div>
          ) : (
            <div className="relative inline-flex">
              <button
                onClick={handleImportBaseMap}
                className="px-3 py-1.5 transition flex items-center gap-2 font-semibold rounded-lg bg-amber-500 hover:bg-amber-600 text-white animate-pulse"
                title={t('topBar.importCAD')}
              >
                <ImageIcon size={16} />
                <span>{t('topBar.importCAD')}</span>
              </button>
            </div>
          )
        )}

        {/* Hidden file inputs */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg"
          onChange={handleFileChange}
          className="hidden"
        />
        <input
          ref={projectFileInputRef}
          type="file"
          accept=".kimap.tm"
          onChange={handleProjectFileChange}
          className="hidden"
        />

        <div className="h-6 w-px bg-gray-300 mx-2" />

        {/* History */}
        <button
          onClick={undo}
          disabled={history.past.length === 0}
          className="p-2 hover:bg-gray-100 rounded transition disabled:opacity-30 disabled:cursor-not-allowed"
          title={t('topBar.undo')}
        >
          <Undo size={16} />
        </button>
        <button
          onClick={redo}
          disabled={history.future.length === 0}
          className="p-2 hover:bg-gray-100 rounded transition disabled:opacity-30 disabled:cursor-not-allowed"
          title={t('topBar.redo')}
        >
          <Redo size={16} />
        </button>

        <div className="h-6 w-px bg-gray-300 mx-2" />

        {/* 3D预览按钮 */}
        <button
          onClick={openPreview}
          disabled={!hasElements}
          data-action="3d-preview"
          className="px-3 py-1.5 text-sm font-semibold hover:bg-gray-100 rounded-lg transition disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1.5 bg-primary-50 text-primary-600 border border-primary-200"
          title={hasElements ? t('topBar.preview3D') : t('topBar.drawFirst')}
        >
          <Eye size={14} />
          {t('topBar.preview3D').split(' (')[0]}
        </button>

        {/* 导出下拉菜单 / 发布地图按钮 */}
        {!isExampleProject && (
          isExternalMode ? (
            <div className="relative inline-flex">
              <button
                onClick={handlePublishMap}
                className="px-3 py-1.5 text-sm font-semibold transition flex items-center gap-1.5 border rounded-lg hover:bg-gray-100 bg-success-50 text-success-600 border-success-200"
                title={t('topBar.publishMap')}
              >
                <Send size={14} />
                <span>{t('topBar.publishMap')}</span>
              </button>
              {!hasPublish && (
                <div
                  className="absolute inset-0 z-10 rounded-lg bg-white/60 backdrop-blur-[1px] flex flex-col items-center justify-center"
                  onClick={(e) => { e.stopPropagation(); }}
                >
                  <Tooltip
                    color="#fff"
                    placement="bottom"
                    title={upgradeTooltipOverlay(['Personal', 'Professional', 'Enterprise'], t('permissionCard.publishMap'))}
                    trigger="hover"
                  >
                    <div className="w-full h-full flex flex-col items-center justify-center gap-1 cursor-pointer">
                      <CrownOutlined className="text-amber-500 text-base" />
                      <span className="text-xs text-amber-600 font-semibold whitespace-nowrap">{t('permissionCard.premium')}</span>
                    </div>
                  </Tooltip>
                </div>
              )}
            </div>
          ) : (
            <div className="relative inline-flex">
              <Dropdown
              menu={{
                items: [
                  {
                    key: 'obj',
                    label: t('projectList.exportOBJ'),
                    icon: <Download size={14} />,
                    onClick: handleExportOBJ,
                    disabled: !hasElements,
                  },
                  {
                    key: 'kimap',
                    label: t('projectList.exportKimap'),
                    icon: <Lock size={14} />,
                    onClick: handleExportKimap,
                    disabled: !hasElements,
                  },
                ] as MenuProps['items'],
              }}
              disabled={!hasElements}
            >
              <button
                className="px-3 py-1.5 text-sm font-semibold rounded-lg transition flex items-center gap-1.5 border hover:bg-gray-100 bg-success-50 text-success-600 border-success-200"
                disabled={!hasElements}
                title={hasElements ? t('topBar.export') : t('topBar.drawFirst')}
              >
                <Download size={14} />
                <span>{t('topBar.export')}</span>
                <ChevronDown size={12} />
              </button>
            </Dropdown>
              {!hasExport && (
                <div
                  className="absolute inset-0 z-10 rounded-lg bg-white/60 backdrop-blur-[1px] flex flex-col items-center justify-center"
                  onClick={(e) => { e.stopPropagation(); }}
                >
                  <Tooltip
                    color="#fff"
                    placement="bottom"
                    title={upgradeTooltipOverlay(['Professional', 'Enterprise'], t('permissionCard.exportOBJ'))}
                    trigger="hover"
                  >
                    <div className="w-full h-full flex flex-col items-center justify-center gap-1 cursor-pointer">
                      <CrownOutlined className="text-amber-500 text-base" />
                      <span className="text-xs text-amber-600 font-semibold whitespace-nowrap">{t('permissionCard.premium')}</span>
                    </div>
                  </Tooltip>
                </div>
              )}
            </div>
          )
        )}

        <div className="h-6 w-px bg-gray-300 mx-2" />

        {/* 底图透明度控制（仅在有底图且有 basemap 权限时显示） */}
        {hasBaseMap && hasBasemapPerm && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-600 text-xs">{t('topBar.adjustOpacity').split('(')[0]}:</span>
            <input
              type="range"
              min="0"
              max="100"
              value={Math.round((project?.mapConfig?.baseMap?.opacity || 0.7) * 100)}
              className="w-20 h-1"
              title={t('topBar.adjustOpacity')}
              onChange={(e) => {
                const opacity = parseInt(e.target.value) / 100;
                updateBaseMap({ opacity });
              }}
            />
            <span className="text-xs text-gray-500 w-8">
              {Math.round((project?.mapConfig?.baseMap?.opacity || 0.7) * 100)}%
            </span>
          </div>
        )}

        <div className="h-6 w-px bg-gray-300 mx-2" />

        {/* 语言切换 */}
        {isExampleProject && <FeedbackButton placement="header" sourcePage="/editor/example" />}
        <LanguageSwitcher />
      </div>

    </div>
  );
};

export default TopBar;
